import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { ADDRESS_SINGLE_LINE, BRAND } from '../../../data/site';
import { HERO_IMAGE } from '../../../data/images';
const EYEBROW = 'Brackenfield · Derbyshire';

/**
 * HeroLanding: eyebrow, one h1, subheading, two actions.
 *
 * Deliberately not full-viewport. A hero filling the screen hides that the
 * page continues. The image is the LCP element, so it is eager with high
 * priority and never lazy-loaded. The header offset lives on `main`, so
 * this section adds none of its own.
 */
export function HeroSection() {
  return <section aria-labelledby="hero-heading" className="relative isolate flex min-h-[520px] w-full items-end overflow-hidden bg-ink-deep py-14 sm:min-h-[580px]" data-id="element-268" data-mp="31r9187yl4ewb">
      <div className="absolute inset-0 -z-10" data-id="element-269" data-mp="2rsjhe9n4okdf">
        <img src={HERO_IMAGE.src} alt={HERO_IMAGE.alt} loading="eager" decoding="sync" {...{
        fetchpriority: 'high'
      }} className="h-full w-full object-cover object-[center_35%]" data-id="element-270" data-mp="2rue3hra4yij3" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/75 to-ink-deep/55" aria-hidden="true" data-id="element-271" data-mp="murdysc13jm" />
      </div>

      <div className="mx-auto w-full max-w-container px-gutter-sm sm:px-gutter-md lg:px-gutter-lg" data-id="element-272" data-mp="3i19nq10r3fxr">
        <div className="max-w-2xl" data-id="element-273" data-mp="3mzurgaf7ohqy">
          <p className="mb-4 border-l-4 border-flame pl-3 text-xs font-black uppercase tracking-[0.22em] text-flame sm:text-sm" data-id="element-274" data-mp="em48d0od41on">
            {EYEBROW}
          </p>

          <h1 id="hero-heading" className="font-bebas text-6xl leading-[0.85] tracking-wider text-cream sm:text-7xl lg:text-8xl" data-id="element-275" data-mp="4o7zkfiwditp">
            {BRAND.name}
          </h1>

          <p className="mt-5 max-w-prose text-lg font-medium leading-relaxed text-cream/85 sm:text-xl" data-id="element-276" data-mp="2peowd0k9z3u7">
            {BRAND.tagline} A gritstone café on the hill road at Brackenfield,
            with a deck that looks clean across the valley, and a bike night
            every single Friday.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-277" data-mp="vvy9bxe4k4n4">
            <Link to="/whats-on" className="group inline-flex items-center justify-center gap-2 rounded-container bg-flame px-6 py-3.5 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 focus:ring-offset-ink-deep" data-id="element-278" data-mp="r4zvd4glzin1">
              See what's on
              <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-1 motion-reduce:transform-none" aria-hidden="true" data-id="element-279" data-mp="85lx4abujlqq" />
            </Link>

            <Link to="/visit" className="inline-flex items-center justify-center gap-2 rounded-container border-2 border-cream/30 bg-white/5 px-6 py-3.5 text-sm font-black uppercase tracking-wide text-cream backdrop-blur-sm transition-colors duration-200 ease-out-expo hover:border-cream/55 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cream focus:ring-offset-2 focus:ring-offset-ink-deep" data-id="element-280" data-mp="22jmt8ng0hun4">
              Find us
              <MapPin className="h-4 w-4" aria-hidden="true" data-id="element-281" data-mp="2jtqq1odi2yz7" />
            </Link>
          </div>

          <address className="mt-7 flex items-start gap-2 text-sm font-medium not-italic text-cream/70" data-id="element-282" data-mp="1m6iaea06kk54">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-flame" aria-hidden="true" data-id="element-283" data-mp="1r40d3inlu34o" />
            <span data-id="element-284" data-mp="1w2bmjs9zmbmd">{ADDRESS_SINGLE_LINE}</span>
          </address>
        </div>
      </div>
    </section>;
}